import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lightning, CircleNotch, ArrowRight } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { requestMeetingAudio, setPendingMeetingAudio, stopStream } from "@/lib/meetingAudio";
import { PrivateNote, PrivateToggle } from "@/components/observer/PrivateMeeting";
import { FocusField } from "@/components/observer/FocusField";

// The main way in: a title and, optionally, what the Observer should help with. Everything else has a default.
export const QuickStart = ({ defaults, privateMode = false, onPrivateChange, idleMinutes = 30, focus, onFocusChange, place = "room", onPlace, recentFocus = [], title, onTitle }) => {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const choose = (p) => onPlace?.(p);

  const go = async () => {
    if (!title.trim()) return toast.error("Give the meeting a title");
    let meeting = null;
    if (place === "online") {
      try {
        meeting = await requestMeetingAudio(); // must run inside the click, before any other await
      } catch (err) {
        return toast.error(err.message);
      }
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("payload", JSON.stringify({ title, quick: true, purpose: focus || "", participants: [], briefing_csv: "", ...defaults, audio_source: place, private: privateMode }));
    try {
      const { data } = await api.post("/sessions", fd);
      if (meeting) setPendingMeetingAudio(data.id, meeting);
      nav(`/session/${data.id}`);
    } catch (err) {
      stopStream(meeting);
      toast.error(errMsg(err));
      setBusy(false);
    }
  };

  return (
    <div className="border border-[var(--o-border)] bg-[var(--o-bg2)] rise" style={{ animationDelay: "80ms" }} data-testid="quick-start">
    <div className="p-5 sm:p-6 flex flex-col md:flex-row md:items-center gap-4">
      <div className="flex items-center gap-3 md:w-64 shrink-0">
        <Lightning size={20} weight="fill" className="text-[#F59E0B]" />
        <div>
          <div className="font-display text-base md:text-lg font-bold">Start a meeting</div>
          <div className="text-xs text-[var(--o-muted2)]">A title, and what you want from the Observer.</div>
        </div>
      </div>
      <div className="flex border border-[var(--o-border)] shrink-0 h-[42px]" role="radiogroup" aria-label="Where is the meeting" title={place === "online" ? "Chrome will ask which tab to share: pick your Meet tab and turn on “Also share tab audio”" : undefined}>
        {[["room", "In the room"], ["online", "Online"]].map(([v, l]) => (
          <button key={v} type="button" role="radio" aria-checked={place === v} onClick={() => choose(v)} className={`px-3 text-xs font-mono transition-colors ${place === v ? "bg-[var(--o-fg)] text-[var(--o-bg)]" : "text-[var(--o-muted2)] hover:text-[var(--o-fg)]"}`} data-testid={`quick-place-${v}`}>
            {l}
          </button>
        ))}
      </div>
      <input
        autoComplete="off"
        value={title}
        onChange={(e) => onTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            go();
          }
        }}
        placeholder="e.g. Pricing huddle"
        className="flex-1 bg-[var(--o-bg)] border border-[var(--o-border)] px-3 py-2.5 text-sm outline-none focus:border-[var(--o-fg)] transition-colors placeholder:text-[var(--o-muted3)]"
        data-testid="quick-title-input"
      />
      <button type="button" onClick={go} disabled={busy} className="h-[42px] px-5 flex items-center justify-center gap-2 bg-[var(--o-fg)] text-[var(--o-bg)] text-sm font-semibold hover:opacity-90 disabled:opacity-40 transition-opacity" data-testid="quick-start-button">
        {busy ? <CircleNotch size={15} className="animate-spin" /> : null} Start now <ArrowRight size={15} weight="bold" />
      </button>
    </div>
    <div className="px-5 sm:px-6 pb-5" data-testid="quick-focus">
      <FocusField value={focus} onChange={onFocusChange} testid="quick-focus" recent={recentFocus} />
    </div>
    <div className="px-5 sm:px-6 pb-5 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-[var(--o-border-soft)] pt-4">
      <PrivateToggle value={privateMode} onChange={onPrivateChange} compact testid="quick-private-toggle" />
      {privateMode ? <PrivateNote idleMinutes={idleMinutes} /> : <span className="text-xs text-[var(--o-muted3)]">Sensitive meeting? Turn on Private and nothing is saved.</span>}
    </div>
    </div>
  );
};
